import { regionLabel } from '@/utils/regionLabel';
import { api } from '@/api/repository/axios';

/**
 * 채용 목록 지역 고르기 — 시·도와 구·군 목록(서버가 주는 행정구역 코드 순서 그대로), 구가 있는 시 묶음, 고른 지역 표기.
 *
 * 지역 키는 `시도id_구id`(`시도id_0` = 시·도 전체). 구가 있는 시(경기 수원시 등)는 서버에 시 자체(`수원시`)와
 * 구(`수원시 장안구`…)가 따로 있다 — "수원시 전체"는 시와 그 구를 모두 고른 것이고, 고른 지역에는 한 칸으로 보인다.
 */

export interface RegionCity {
    id: string;
    name: string;
}

export interface RegionDistrict {
    id: string;
    /** 서버 이름(앞뒤 공백 정리) — 구가 있는 시의 구는 "성남시 분당구" */
    name: string;
    /** 칸에 보일 이름 — 구가 있는 시의 구는 구 이름만("분당구") */
    shortName: string;
    cityId: string;
    key: string;
}

/** 구가 있는 시 한 묶음 — "{시} 전체" + 구 칸 */
export interface RegionGroup {
    name: string;
    /** 시 자체(서버에 있으면) */
    parent: RegionDistrict | null;
    children: RegionDistrict[];
}

export interface CityRegions {
    city: RegionCity;
    /** 구 없는 시·군·구(묶음 밖) — 코드 순 */
    singles: RegionDistrict[];
    /** 구가 있는 시 묶음 — 코드 순 */
    groups: RegionGroup[];
    /** 이 시·도의 모든 지역(키로 찾기용) */
    all: RegionDistrict[];
}

export interface RegionData {
    cities: RegionCity[];
    byCity: Record<string, CityRegions>;
    /** 지역 키 → 지역 */
    byKey: Record<string, RegionDistrict>;
}

interface RawItem {
    id: string | number;
    name: string;
}

export const cityAllKey = (cityId: string): string => `${cityId}_0`;

/** 서버 목록으로 시·도별 묶음을 만든다(순서는 받은 그대로) */
export const buildRegionData = (rawCities: RawItem[], rawDistricts: Record<string, RawItem[]>): RegionData => {
    const cities: RegionCity[] = rawCities
        .map((city: RawItem) => ({ id: String(city.id), name: city.name.trim() }))
        .filter((city: RegionCity) => city.id !== '0');
    const byCity: Record<string, CityRegions> = {};
    const byKey: Record<string, RegionDistrict> = {};
    cities.forEach((city: RegionCity) => {
        const list: RegionDistrict[] = (rawDistricts[city.id] ?? [])
            .filter((item: RawItem) => String(item.id) !== '0')
            .map((item: RawItem) => {
                const name: string = item.name.trim().replace(/\s+/g, ' ');
                const space: number = name.indexOf(' ');
                return {
                    id: String(item.id),
                    name,
                    shortName: space > 0 ? name.slice(space + 1) : name,
                    cityId: city.id,
                    key: `${city.id}_${String(item.id)}`,
                };
            });
        const parentNames: string[] = [];
        list.forEach((district: RegionDistrict) => {
            const space: number = district.name.indexOf(' ');
            if (space > 0) {
                const parentName: string = district.name.slice(0, space);
                if (!parentNames.includes(parentName)) parentNames.push(parentName);
            }
        });
        const singles: RegionDistrict[] = list.filter((district: RegionDistrict) => !district.name.includes(' ') && !parentNames.includes(district.name));
        const groups: RegionGroup[] = parentNames.map((parentName: string) => ({
            name: parentName,
            parent: list.find((district: RegionDistrict) => district.name === parentName) ?? null,
            children: list.filter((district: RegionDistrict) => district.name.startsWith(`${parentName} `)),
        }));
        byCity[city.id] = { city, singles, groups, all: list };
        list.forEach((district: RegionDistrict) => { byKey[district.key] = district; });
    });
    return { cities, byCity, byKey };
};

let regionDataPromise: Promise<RegionData> | null = null;

/** 시·도와 모든 구·군을 한 번에 받는다(한 세션에 한 번, 실패하면 다음에 다시) */
export const loadRegionData = (): Promise<RegionData> => {
    if (!regionDataPromise) {
        regionDataPromise = (async (): Promise<RegionData> => {
            const citiesResponse = await api.search.getCities();
            const rawCities: RawItem[] = (citiesResponse?.result ?? []) as unknown as RawItem[];
            const ids: string[] = rawCities.map((city: RawItem) => String(city.id)).filter((id: string) => id !== '0');
            const districtsResponse = await api.search.getDistrictsBatch(ids);
            const rawDistricts: Record<string, RawItem[]> = (districtsResponse?.result ?? {}) as unknown as Record<string, RawItem[]>;
            return buildRegionData(rawCities, rawDistricts);
        })().catch((error: unknown) => {
            regionDataPromise = null;
            throw error;
        });
    }
    return regionDataPromise;
};

/** 고른 지역 한 칸(시트 아래 "고른 지역", 칩 줄 요약) — ✕ 로 [keys] 를 한꺼번에 뺀다 */
export interface RegionPick {
    id: string;
    label: string;
    cityId: string;
    keys: string[];
}

const cityOfKey = (key: string): string => key.split('_')[0];

const groupKeys = (group: RegionGroup): string[] => [...(group.parent ? [group.parent.key] : []), ...group.children.map((child: RegionDistrict) => child.key)];

/** 이 묶음(구가 있는 시)을 통째로 골랐는지 — 시 자체와 모든 구 */
export const isGroupPicked = (group: RegionGroup, selected: string[]): boolean =>
    groupKeys(group).every((key: string) => selected.includes(key));

/**
 * 고른 지역 키 → 보이는 칸 목록. 고른 순서를 따른다.
 * "서울 전체", "서울 강남구", "성남시 전체", "성남시 분당구", 모르는 키(목록을 아직 못 받음)는 "지역"
 */
export const regionPicks = (selected: string[], data: RegionData | null): RegionPick[] => {
    const picks: RegionPick[] = [];
    const used: Set<string> = new Set();
    selected.forEach((key: string) => {
        if (used.has(key)) return;
        const cityId: string = cityOfKey(key);
        const cityRegions: CityRegions | undefined = data?.byCity[cityId];
        if (key === cityAllKey(cityId)) {
            used.add(key);
            picks.push({ id: key, label: `${cityRegions?.city.name ?? '지역'} 전체`, cityId, keys: [key] });
            return;
        }
        const district: RegionDistrict | undefined = data?.byKey[key];
        if (!district || !cityRegions) {
            used.add(key);
            picks.push({ id: key, label: '지역', cityId, keys: [key] });
            return;
        }
        const group: RegionGroup | undefined = cityRegions.groups.find((item: RegionGroup) =>
            item.parent?.key === key || item.children.some((child: RegionDistrict) => child.key === key));
        if (group && isGroupPicked(group, selected)) {
            const keys: string[] = groupKeys(group);
            keys.forEach((item: string) => used.add(item));
            picks.push({ id: `group:${cityId}:${group.name}`, label: `${group.name} 전체`, cityId, keys });
            return;
        }
        used.add(key);
        // 구가 있는 시의 구는 "성남시 분당구", 통합시 안 구는 "광주 동구", 세종은 "세종시", 그 밖은 "서울 강남구"
        const label: string = district.name.includes(' ') ? district.name : regionLabel(cityRegions.city.name, district.name);
        picks.push({ id: key, label, cityId, keys: [key] });
    });
    return picks;
};

/** 칩 줄 지역 칩 글자 — 없으면 "전국", 하나면 그 이름, 여러 개면 "서울 강남구 외 1" */
export const regionChipLabel = (selected: string[], data: RegionData | null): string => {
    const picks: RegionPick[] = regionPicks(selected, data);
    if (picks.length === 0) return '전국';
    return picks.length === 1 ? picks[0].label : `${picks[0].label} 외 ${picks.length - 1}`;
};

// ───────── 고르기 동작 (새 선택 목록을 돌려준다) ─────────

const withoutCity = (selected: string[], cityId: string): string[] => selected.filter((key: string) => cityOfKey(key) !== cityId);

/** "{시·도} 전체" — 켜면 그 시·도의 다른 선택은 푼다 */
export const toggleCityAll = (selected: string[], cityId: string): string[] =>
    selected.includes(cityAllKey(cityId)) ? selected.filter((key: string) => key !== cityAllKey(cityId)) : [...withoutCity(selected, cityId), cityAllKey(cityId)];

/** "{시} 전체"(구가 있는 시) — 시 자체와 모든 구를 같이 켜고 끈다 */
export const toggleGroup = (selected: string[], group: RegionGroup, cityId: string): string[] => {
    const keys: string[] = groupKeys(group);
    const base: string[] = selected.filter((key: string) => key !== cityAllKey(cityId));
    if (isGroupPicked(group, selected)) return base.filter((key: string) => !keys.includes(key));
    return [...base, ...keys.filter((key: string) => !base.includes(key))];
};

/**
 * 구·군 한 칸 — 켜면 "{시·도} 전체"는 풀린다. 묶음의 구를 끄면 "{시} 전체"(시 자체)도 풀리고,
 * 묶음의 구를 다 켜면 시 자체도 켜서 "{시} 전체" 한 칸이 된다.
 */
export const toggleDistrict = (selected: string[], district: RegionDistrict, data: RegionData): string[] => {
    const cityId: string = district.cityId;
    const group: RegionGroup | undefined = data.byCity[cityId]?.groups.find((item: RegionGroup) =>
        item.children.some((child: RegionDistrict) => child.key === district.key));
    const base: string[] = selected.filter((key: string) => key !== cityAllKey(cityId));
    if (base.includes(district.key)) {
        return base.filter((key: string) => key !== district.key && key !== group?.parent?.key);
    }
    const next: string[] = [...base, district.key];
    if (group?.parent && group.children.every((child: RegionDistrict) => next.includes(child.key)) && !next.includes(group.parent.key)) {
        next.push(group.parent.key);
    }
    return next;
};

/** 시·도별 고른 칸 수(왼쪽 시·도 목록의 숫자) */
export const pickCountByCity = (selected: string[], data: RegionData | null): Record<string, number> =>
    regionPicks(selected, data).reduce<Record<string, number>>((acc: Record<string, number>, pick: RegionPick) => {
        acc[pick.cityId] = (acc[pick.cityId] ?? 0) + 1;
        return acc;
    }, {});
